// pro.rejouis.fr — commandes par catégories de jouets reconditionnés hors boutique (grille HB).
// Pas de paiement en ligne : la commande part à l'équipe Rejouis, qui la prépare et la facture.
// Les montants affichés ici reprennent la règle du lab (_commandes.js calculer), qui fait foi.
import { useEffect, useMemo, useState } from 'react';
import { pro, fmtDate, fmtEuro } from './lib/api.js';
import { naviguer } from './lib/nav.js';
import { Icons, Spinner, Retour } from './components/ui.jsx';
import { resumeAdresse } from './Coordonnees.jsx';

const PANIER_KEY = 'pro_panier';
export const STATUTS_COMMANDE = {
  'À préparer': { label: 'Reçue', badge: 'badge-creme' },
  'Préparée': { label: 'En préparation', badge: 'badge-mint' },
  'Expédiée': { label: 'Expédiée', badge: 'badge-mint' },
  'Facturée': { label: 'Facturée', badge: 'badge-stone' },
  'Annulée': { label: 'Annulée', badge: 'badge-stone' },
};

function lirePanier() {
  try { return JSON.parse(localStorage.getItem(PANIER_KEY)) || {}; } catch { return {}; }
}
function ecrirePanier(p) {
  try { localStorage.setItem(PANIER_KEY, JSON.stringify(p)); } catch { /* navigation privée */ }
}

// Même règle que le lab (_commandes.js fraisPort) : grille Chronopost du poids estimé + marge,
// arrondi à l'euro supérieur ; au-delà du dernier palier, plusieurs colis égaux.
function fraisPort(nbJouets, sousTotal, p) {
  if (!nbJouets || sousTotal >= p.offertDes) return { port: 0, poidsKg: 0 };
  const poidsKg = nbJouets * p.poidsJouetKg;
  const max = p.grille[p.grille.length - 1][0];
  const colis = Math.ceil((poidsKg + p.poidsCartonKg) / max);
  const parColis = poidsKg / colis + p.poidsCartonKg;
  const tarif = (p.grille.find(([kg]) => parColis <= kg) || p.grille[p.grille.length - 1])[1];
  return { port: Math.ceil(tarif * colis * (1 + p.marge)), poidsKg: Math.round((poidsKg + colis * p.poidsCartonKg) * 10) / 10 };
}

function calculer(cat, quantites, { solde, utiliserBon, port }) {
  const lignes = cat.filter((c) => quantites[c.id] > 0).map((c) => ({ ...c, qte: quantites[c.id], total: quantites[c.id] * c.prix }));
  const nbJouets = lignes.reduce((s, l) => s + l.qte, 0);
  const sousTotal = lignes.reduce((s, l) => s + l.total, 0);
  const { port: frais, poidsKg } = fraisPort(nbJouets, sousTotal, port);
  const bon = utiliserBon && solde > 0 ? Math.min(solde, sousTotal + frais) : 0;
  return { lignes, nbJouets, sousTotal, port: frais, poidsKg, bon, total: sousTotal + frais - bon };
}

function Stepper({ valeur, onChange }) {
  const borne = (v) => Math.max(0, Math.min(500, Math.round(Number(v) || 0)));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <button className="chip" style={{ width: 38, height: 38, justifyContent: 'center', fontSize: 18, padding: 0 }} aria-label="Moins" onClick={() => onChange(borne(valeur - 1))}>−</button>
      <input className="field" type="number" inputMode="numeric" min={0} value={valeur || ''} placeholder="0"
        onChange={(e) => onChange(borne(e.target.value))} style={{ width: 62, textAlign: 'center', fontWeight: 700, fontSize: 16, padding: '8px 6px' }} />
      <button className="chip" style={{ width: 38, height: 38, justifyContent: 'center', fontSize: 18, padding: 0 }} aria-label="Plus" onClick={() => onChange(borne(valeur + 1))}>+</button>
    </div>
  );
}

const Ligne = ({ label, valeur, fort, couleur }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '4px 0', fontSize: fort ? 17 : 14, fontWeight: fort ? 800 : 400, color: couleur }}>
    <span>{label}</span><span style={{ whiteSpace: 'nowrap' }}>{valeur}</span>
  </div>
);

export function Commander({ onFait }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [quantites, setQuantites] = useState(lirePanier);
  const [utiliserBon, setUtiliserBon] = useState(false);
  const [message, setMessage] = useState('');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => { pro('catalogue').then(setData).catch((e) => setErreur(e.message)); }, []);
  useEffect(() => { ecrirePanier(quantites); }, [quantites]);

  const solde = data?.partenaire?.solde || 0;
  // La barre du bas ne sert qu'à descendre au récapitulatif : masquée quand il est à l'écran.
  const [recapVisible, setRecapVisible] = useState(false);
  useEffect(() => {
    const el = document.getElementById('recap');
    if (!el || !window.IntersectionObserver) return undefined;
    const obs = new IntersectionObserver(([e]) => setRecapVisible(e.isIntersecting), { threshold: 0.05 });
    obs.observe(el);
    return () => obs.disconnect();
  });
  const r = useMemo(() => (data ? calculer(data.catalogue, quantites, { solde, utiliserBon, port: data.port }) : null), [data, quantites, solde, utiliserBon]);

  async function valider() {
    if (!window.confirm(`Envoyer votre commande de ${r.nbJouets} jouets (${fmtEuro(r.total)}) ?\n\nNous la préparons et vous envoyons la facture.`)) return;
    setEnCours(true); setErreur(null);
    try {
      const { commande } = await pro('commander', {
        lignes: r.lignes.map((l) => ({ id: l.id, qte: l.qte })), utiliserBon, message,
      });
      setQuantites({}); ecrirePanier({});
      await onFait(commande.ref);
    } catch (e) { setErreur(e.message); setEnCours(false); }
  }

  if (erreur && !data) return <div className="t-stone" style={{ padding: '60px 0', textAlign: 'center' }}>{erreur}</div>;
  if (!data) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spinner size={22} label="Chargement du catalogue…" /></div>;
  const manque = data.port.offertDes - r.sousTotal;

  return (
    <div className="anim-pop" style={{ paddingBottom: r.nbJouets ? 90 : 0 }}>
      <Retour />
      <h1 className="t-display" style={{ fontSize: 26, margin: '0 0 6px' }}>Commander des jouets</h1>
      <p className="t-stone" style={{ fontSize: 14.5, lineHeight: 1.6, margin: '0 0 16px' }}>
        Des jouets reconditionnés par Rejouis, choisis par catégorie : nous composons le lot pour vous.
        Prix TTC (TVA sur la marge), port offert dès {fmtEuro(data.port.offertDes)}.
      </p>

      <div style={{ display: 'grid', gap: 10 }}>
        {data.catalogue.map((c) => (
          <div key={c.id} className="card" style={{ padding: 16, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
            borderColor: quantites[c.id] ? 'var(--bleu)' : undefined }}>
            {c.photo ? <img src={c.photo} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 12 }} /> : null}
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{c.categorie}</span>
                <span style={{ fontWeight: 800, color: 'var(--rouge)' }}>{fmtEuro(c.prix)}</span>
              </div>
              <div className="t-stone" style={{ fontSize: 13, lineHeight: 1.5, marginTop: 2 }}>{c.details}</div>
              {c.prixConseille ? <div className="t-stone" style={{ fontSize: 12, marginTop: 4 }}>Prix de vente conseillé : {fmtEuro(c.prixConseille)}</div> : null}
            </div>
            <Stepper valeur={quantites[c.id] || 0} onChange={(v) => setQuantites((q) => ({ ...q, [c.id]: v }))} />
          </div>
        ))}
      </div>

      {r.nbJouets ? (
        <div id="recap" className="card" style={{ padding: 20, marginTop: 18 }}>
          <div className="t-overline" style={{ marginBottom: 8 }}>Votre commande</div>
          {r.lignes.map((l) => <Ligne key={l.id} label={`${l.qte} × ${l.categorie}`} valeur={fmtEuro(l.total)} />)}
          <div className="hr" style={{ margin: '8px 0' }} />
          <Ligne label={`Sous-total (${r.nbJouets} jouets)`} valeur={fmtEuro(r.sousTotal)} />
          <Ligne label={`Frais de port${r.port ? ` (${data.port.transporteur}, ≈ ${String(r.poidsKg).replace('.', ',')} kg)` : ''}`} valeur={r.port ? fmtEuro(r.port) : 'offerts'} />
          {r.port ? (
            <div style={{ fontSize: 12.5, color: 'var(--bleu)', background: 'var(--bleu-pale)', borderRadius: 10, padding: '8px 10px', margin: '4px 0 6px' }}>
              Plus que <b>{fmtEuro(manque)}</b> pour avoir le port offert.
            </div>
          ) : null}
          {solde > 0 ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0', fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={utiliserBon} onChange={(e) => setUtiliserBon(e.target.checked)} style={{ width: 18, height: 18 }} />
              Utiliser mon bon d’achat sur cette commande ({fmtEuro(solde)} disponibles)
            </label>
          ) : null}
          {r.bon ? <Ligne label="Bon d’achat" valeur={`− ${fmtEuro(r.bon)}`} couleur="var(--bleu)" /> : null}
          <div className="hr" style={{ margin: '8px 0' }} />
          <Ligne label="Total" valeur={fmtEuro(r.total)} fort />

          <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 12, background: 'var(--creme)', fontSize: 13.5, lineHeight: 1.5 }}>
            <div className="t-overline">Livraison</div>
            {data.partenaire.coordonneesCompletes ? (
              <div>{resumeAdresse(data.partenaire.coordonnees)}{' '}
                <a href="/coordonnees?retour=commander" onClick={(e) => { e.preventDefault(); naviguer('/coordonnees?retour=commander'); }}
                  style={{ color: 'var(--rouge)', fontWeight: 600 }}>Modifier</a></div>
            ) : <div style={{ color: 'var(--rouge-sombre)' }}>Vos coordonnées de livraison ne sont pas encore renseignées.</div>}
          </div>

          <textarea className="field" rows={3} maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} style={{ marginTop: 12 }}
            placeholder="Un mot pour l’équipe (facultatif) : préférences de couleurs, marques, date souhaitée…" />
          {data.partenaire.coordonneesCompletes ? (
            <button className="btn btn-primary btn-block" style={{ marginTop: 14 }} disabled={enCours} onClick={valider}>
              {enCours ? <Spinner size={16} label="Envoi de la commande…" /> : 'Valider ma commande'}
            </button>
          ) : (
            <button className="btn btn-primary btn-block" style={{ marginTop: 14 }} onClick={() => naviguer('/coordonnees?retour=commander')}>
              Renseigner mes coordonnées
            </button>
          )}
          <p className="t-stone" style={{ fontSize: 12.5, lineHeight: 1.55, margin: '10px 0 0', textAlign: 'center' }}>
            Pas de paiement maintenant : nous préparons votre commande et vous envoyons la facture.
          </p>
          {erreur ? <div style={{ color: 'var(--rouge-sombre)', fontSize: 13, marginTop: 8 }}>{erreur}</div> : null}
        </div>
      ) : null}

      {r.nbJouets && !recapVisible ? (
        <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, background: 'var(--blanc)', boxShadow: 'var(--shadow-bar)', zIndex: 20 }}>
          <div style={{ maxWidth: 760, margin: '0 auto', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 17 }}>{fmtEuro(r.total)}</div>
              <div className="t-stone" style={{ fontSize: 12 }}>{r.nbJouets} jouets · port {r.port ? fmtEuro(r.port) : 'offert'}</div>
            </div>
            <button className="btn btn-primary" style={{ padding: '13px 20px' }}
              onClick={() => document.getElementById('recap')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Voir ma commande</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function CarteCommande({ c }) {
  const st = STATUTS_COMMANDE[c.statut] || { label: c.statut, badge: 'badge-stone' };
  return (
    <button className="card" onClick={() => naviguer(`/commande/${encodeURIComponent(c.ref)}`)}
      style={{ display: 'block', width: '100%', textAlign: 'left', padding: 16, border: '1px solid var(--bord)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontWeight: 700, fontSize: 14.5 }}>Commande du {fmtDate(c.date)}</span>
        <span className={`badge ${st.badge}`}>{st.label}</span>
        <span style={{ marginLeft: 'auto', color: 'var(--stone)' }}><Icons.chev size={18} /></span>
      </div>
      <div className="t-stone" style={{ fontSize: 13, marginTop: 6 }}>
        {c.nbJouets} jouets · {fmtEuro(c.total)}{c.suivi ? <> · <Icons.truck size={13} style={{ verticalAlign: '-2px' }} /> {c.suivi.libelle}</> : null}
      </div>
    </button>
  );
}

export function DetailCommande({ c }) {
  const st = STATUTS_COMMANDE[c.statut] || { label: c.statut, badge: 'badge-stone' };
  return (
    <div className="anim-pop">
      <Retour />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h1 className="t-display" style={{ fontSize: 26, margin: 0 }}>Commande du {fmtDate(c.date)}</h1>
        <span className={`badge ${st.badge}`}>{st.label}</span>
      </div>
      <div className="t-stone mono" style={{ fontSize: 12, marginTop: 4 }}>{c.ref}</div>
      {c.statut === 'À préparer' ? (
        <p className="t-stone" style={{ fontSize: 14, lineHeight: 1.6 }}>Merci ! Nous avons bien reçu votre commande : nous la préparons et vous envoyons la facture.</p>
      ) : null}
      <div className="card" style={{ padding: 20, marginTop: 14 }}>
        {c.lignes.map((l) => <Ligne key={l.id} label={`${l.qte} × ${l.categorie}`} valeur={fmtEuro(l.total)} />)}
        <div className="hr" style={{ margin: '8px 0' }} />
        <Ligne label={`Sous-total (${c.nbJouets} jouets)`} valeur={fmtEuro(c.sousTotal)} />
        <Ligne label="Frais de port" valeur={c.port ? fmtEuro(c.port) : 'offerts'} />
        {c.bon ? <Ligne label="Bon d’achat" valeur={`− ${fmtEuro(c.bon)}`} couleur="var(--bleu)" /> : null}
        <div className="hr" style={{ margin: '8px 0' }} />
        <Ligne label="Total" valeur={fmtEuro(c.total)} fort />
      </div>
      {c.suivi ? (
        <div className="card" style={{ padding: 18, marginTop: 12 }}>
          <div className="t-overline" style={{ marginBottom: 6 }}>Livraison</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ color: 'var(--rouge)' }}><Icons.truck size={22} /></span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{c.suivi.libelle}</div>
              <div className="t-stone" style={{ fontSize: 12.5 }}>
                {c.suivi.transporteur}{c.suivi.livraisonPrevue ? ` · livraison prévue le ${fmtDate(c.suivi.livraisonPrevue)}` : ''}
              </div>
            </div>
          </div>
          {c.suivi.colis.map((p) => (
            <div key={p.numero} style={{ fontSize: 13, marginTop: 8 }}>
              Colis <span className="mono">{p.numero}</span>
              {p.url ? <> · <a href={p.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--rouge)', fontWeight: 600 }}>suivre sur Chronopost</a></> : null}
            </div>
          ))}
        </div>
      ) : null}

      {c.message ? (
        <div className="card" style={{ padding: 18, marginTop: 12 }}>
          <div className="t-overline" style={{ marginBottom: 6 }}>Votre message</div>
          <div style={{ fontSize: 14.5, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{c.message}</div>
        </div>
      ) : null}
      {c.factures?.length ? (
        <div className="card" style={{ padding: 18, marginTop: 12 }}>
          <div className="t-overline" style={{ marginBottom: 8 }}>Facture</div>
          {c.factures.map((f) => (
            <a key={f.url} className="btn btn-secondary btn-block" href={f.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', marginTop: 6 }}>
              <Icons.download size={16} /> {f.nom || 'Télécharger la facture'}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}
